import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BellRing, Printer, MessageCircle, Mail } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from 'recharts'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import toast from 'react-hot-toast'
import { EUR } from '../../constants/red'
import { redApi, dashboardApi, operatoriAvvisoApi, whatsappApi } from '../../services/api'
import ExportXlsxButton from '../../components/red/ExportXlsxButton'

interface WhatsappContatto { id: string; nome: string; email?: string | null; telefono_whatsapp?: string | null; attivo: boolean }

let _logoPdfCache: Promise<string | null> | null = null
function getLogoDataUrl(): Promise<string | null> {
  if (!_logoPdfCache) {
    _logoPdfCache = fetch('/logo-xsophia.png')
      .then(r => r.blob())
      .then(blob => new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(String(reader.result))
        reader.onerror = reject
        reader.readAsDataURL(blob)
      }))
      .catch(() => null)
  }
  return _logoPdfCache
}

const COLORS = ['#1E40AF', '#F97316', '#10b981', '#ef4444', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899']

interface GraficoVoce { label: string; value: number }
interface ComuneVoce extends GraficoVoce { immobili: string[] }
interface GraficiRedData {
  per_fase: GraficoVoce[]
  per_comune: ComuneVoce[]
  per_livello_operativo: GraficoVoce[]
  per_stato_contrattuale: GraficoVoce[]
  prezzo_per_comune: GraficoVoce[]
  pagamenti_per_tipologia: GraficoVoce[]
  acconti_pagati_vs_residui: GraficoVoce[]
  pagamenti_per_mese: { mese: string; previsto: number; effettivo: number }[]
  sal_per_stato: GraficoVoce[]
  documenti_per_stato: GraficoVoce[]
  documenti_per_tipologia: GraficoVoce[]
  scadenze_per_stato: GraficoVoce[]
  professionisti_top: GraficoVoce[]
}

interface KPI {
  n_immobili: number
  n_per_fase: Record<string, number>
  n_per_stato_contrattuale: Record<string, number>
  totale_acquisizione: number
  totale_prezzi_acquisto: number
  totale_imposte: number
  totale_spese_notarili: number
  totale_costi_extra: number
  totale_acconti_pagati: number
  totale_acconti_residui: number
  totale_saldi_residui: number
  dettaglio_residuo: { immobile_id: string; nome: string; importo: number }[]
  totale_usr_approvato: number
  totale_erogato_usr: number
  totale_contributi_concessi: number
  residuo_usr: number
  totale_penali_maturate: number
}

interface AlertItem {
  id: string
  titolo: string
  data_scadenza: string
  giorni_mancanti: number
  urgenza: 'critica' | 'alta' | 'media' | 'normale'
  immobile_id: string
  immobile_nome: string
  immobile_comune: string
}

interface AgendaNota {
  id: string
  titolo: string
  tipo: string
  data_ora: string
  immobile_id: string
  immobile_nome: string
}

interface PagamentoScadenza {
  id: string
  immobile_nome: string
  tipologia: string
  importo: number
  data_prevista: string
  giorni_mancanti: number
  in_ritardo: boolean
}

const URGENZA_CONFIG = {
  critica: { label: 'SCADUTA',     bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300'    },
  alta:    { label: '≤ 7 giorni',  bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  media:   { label: '≤ 14 giorni', bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  normale: { label: '≤ 30 giorni', bg: 'bg-blue-50',    text: 'text-blue-800',   border: 'border-blue-200'   },
}

const RedDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [kpi, setKpi] = useState<KPI | null>(null)
  const [alerts, setAlerts] = useState<AlertItem[]>([])
  const [pagamenti, setPagamenti] = useState<PagamentoScadenza[]>([])
  const [agendaNote, setAgendaNote] = useState<AgendaNota[]>([])
  const [error, setError] = useState<string | null>(null)

  const kpiRef = useRef<HTMLDivElement>(null)
  const graficiRef = useRef<HTMLDivElement>(null)
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [invioModalOpen, setInvioModalOpen] = useState(false)
  const [canaleInvio, setCanaleInvio] = useState<'whatsapp' | 'email'>('whatsapp')
  const [contattiInvio, setContattiInvio] = useState<WhatsappContatto[]>([])
  const [destinatariSelezionati, setDestinatariSelezionati] = useState<Set<string>>(new Set())
  const [sendingInvio, setSendingInvio] = useState(false)

  // Le classi Tailwind md:/lg:grid-cols-N valutano il viewport reale del
  // dispositivo (via media query), non un parametro passato a html2canvas:
  // su telefono senza "richiedi sito desktop" il layout catturato resta a
  // colonna singola anche forzando windowWidth (il meta viewport
  // width=device-width della pagina vince). Non basta forzare solo il
  // numero di colonne via gridTemplateColumns: la larghezza reale del
  // contenitore resta comunque quella (stretta) del mobile, quindi le
  // colonne risultano schiacciate. Serve forzare anche la LARGHEZZA
  // dell'elemento, e per farlo senza essere tagliati dal contenitore padre
  // (`overflow-hidden` nel layout dell'app) lo si sgancia temporaneamente
  // dal flusso con position:fixed fuori schermo, dove può espandersi
  // liberamente a una larghezza "desktop" prima della cattura.
  const DESKTOP_WIDTH = 1400
  const forzaLayoutDesktop = (root: HTMLElement) => {
    const griglie = [root, ...Array.from(root.querySelectorAll<HTMLElement>('.grid'))]
    const modificati: { el: HTMLElement; originale: string }[] = []
    for (const el of griglie) {
      if (!el.classList.contains('grid')) continue
      const match = el.className.match(/(?:md|lg):grid-cols-(\d+)/)
      if (!match) continue
      modificati.push({ el, originale: el.style.gridTemplateColumns })
      el.style.gridTemplateColumns = `repeat(${match[1]}, minmax(0, 1fr))`
    }
    const stileOriginale = root.getAttribute('style')
    root.style.position = 'fixed'
    root.style.top = '0'
    root.style.left = '-10000px'
    root.style.width = `${DESKTOP_WIDTH}px`
    root.style.zIndex = '-1'
    return () => {
      modificati.forEach(({ el, originale }) => { el.style.gridTemplateColumns = originale })
      if (stileOriginale === null) root.removeAttribute('style')
      else root.setAttribute('style', stileOriginale)
    }
  }

  const generaPdfA3 = async (): Promise<{ pdf: jsPDF; base64: string } | null> => {
    const kpiEl = kpiRef.current, graficiEl = graficiRef.current
    if (!kpiEl || !graficiEl) return null
    // KPI e grafici non sono adiacenti nel layout (Agenda RED sta in mezzo,
    // posizione voluta): li catturo separatamente e li impilo in un'unica
    // immagine composita, escludendo così le sezioni-lista dal PDF.
    const ripristinaKpi = forzaLayoutDesktop(kpiEl)
    const ripristinaGrafici = forzaLayoutDesktop(graficiEl)
    // I grafici Recharts (ResponsiveContainer) si ridimensionano in modo
    // asincrono via ResizeObserver dopo il cambio di griglia/larghezza: senza
    // attendere il reflow, html2canvas cattura gli SVG ancora alla vecchia
    // larghezza (colonna singola), riproducendo lo stesso problema.
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 150)))
    })
    let kpiCanvas: HTMLCanvasElement, graficiCanvas: HTMLCanvasElement
    try {
      [kpiCanvas, graficiCanvas] = await Promise.all([
        html2canvas(kpiEl, { backgroundColor: '#f0f2f5', scale: 1.5 }),
        html2canvas(graficiEl, { backgroundColor: '#f0f2f5', scale: 1.5 }),
      ])
    } finally {
      ripristinaKpi()
      ripristinaGrafici()
    }
    const width = Math.max(kpiCanvas.width, graficiCanvas.width)
    const gap = 24
    const composite = document.createElement('canvas')
    composite.width = width
    composite.height = kpiCanvas.height + gap + graficiCanvas.height
    const cctx = composite.getContext('2d')
    if (!cctx) throw new Error('canvas context non disponibile')
    cctx.fillStyle = '#f0f2f5'
    cctx.fillRect(0, 0, composite.width, composite.height)
    cctx.drawImage(kpiCanvas, 0, 0)
    cctx.drawImage(graficiCanvas, 0, kpiCanvas.height + gap)
    const canvas = composite
    // A4 verticale: 210x297mm. Immagine scalata per riempire il foglio
    // mantenendo le proporzioni, centrata, con margine di 8mm. Una fascia in
    // alto è riservata al piccolo logo XSophia in alto a sinistra.
    const logoDataUrl = await getLogoDataUrl()
    const pageW = 210, pageH = 297, margin = 8
    const logoW = 22, logoH = logoDataUrl ? 22 * (889 / 1770) : 0
    const headerReserve = logoDataUrl ? logoH + 4 : 0
    const availW = pageW - margin * 2, availH = pageH - margin * 2 - headerReserve
    const imgRatio = canvas.width / canvas.height
    let w = availW, h = w / imgRatio
    if (h > availH) { h = availH; w = h * imgRatio }
    const x = (pageW - w) / 2, y = margin + headerReserve + (availH - h) / 2
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true })
    if (logoDataUrl) pdf.addImage(logoDataUrl, 'PNG', margin, margin, logoW, logoH)
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.85), 'JPEG', x, y, w, h)
    const base64 = pdf.output('datauristring').split(',')[1]
    return { pdf, base64 }
  }

  const handleStampaPdf = async () => {
    setGeneratingPdf(true)
    try {
      const res = await generaPdfA3()
      if (!res) return
      res.pdf.save(`dashboard-red_${new Date().toISOString().slice(0, 10)}.pdf`)
    } catch {
      toast.error('Errore durante la generazione del PDF')
    } finally {
      setGeneratingPdf(false)
    }
  }

  const apriModalInvio = async (canale: 'whatsapp' | 'email') => {
    setCanaleInvio(canale)
    setDestinatariSelezionati(new Set())
    setInvioModalOpen(true)
    try {
      const res = await operatoriAvvisoApi.list()
      const campo = canale === 'whatsapp' ? 'telefono_whatsapp' : 'email'
      setContattiInvio(res.data.filter((o: WhatsappContatto) => o[campo]))
    } catch {
      toast.error('Errore caricamento operatori avviso')
    }
  }

  const toggleContattoInvio = (id: string) => setDestinatariSelezionati(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const handleInvia = async () => {
    if (destinatariSelezionati.size === 0) return
    setSendingInvio(true)
    try {
      const res = await generaPdfA3()
      if (!res) return
      const filename = `dashboard-red_${new Date().toISOString().slice(0, 10)}.pdf`
      const destinatari = contattiInvio.filter(c => destinatariSelezionati.has(c.id))
      if (canaleInvio === 'whatsapp') {
        let ok = 0
        for (const c of destinatari) {
          try {
            await whatsappApi.sendDocument(c.telefono_whatsapp as string, res.base64, filename, 'Dashboard RED — Ricostruzione Marche')
            ok++
          } catch {
            toast.error(`Invio fallito a ${c.nome}`)
          }
        }
        if (ok > 0) toast.success(`PDF inviato a ${ok} destinatari`)
      } else {
        const resp = await redApi.export.inviaEmailAllegato({
          file_base64: res.base64,
          filename,
          oggetto: 'XSophia — Dashboard RED Ricostruzione Marche',
          corpo: 'In allegato il PDF della Dashboard RED.',
          destinatari: destinatari.map(c => c.email as string),
        })
        if (resp.data.inviati > 0) toast.success(`PDF inviato via email a ${resp.data.inviati} destinatari`)
        if (resp.data.inviati < resp.data.totale) toast.error(`${resp.data.totale - resp.data.inviati} invii falliti`)
      }
      setInvioModalOpen(false)
      setDestinatariSelezionati(new Set())
    } catch {
      toast.error('Errore durante la generazione del PDF')
    } finally {
      setSendingInvio(false)
    }
  }

  const { data: grafici } = useQuery<GraficiRedData>({
    queryKey: ['red-dashboard-grafici'],
    queryFn: () => redApi.dashboard.grafici().then(r => r.data),
    refetchInterval: 60_000,
  })

  useEffect(() => {
    Promise.all([
      redApi.dashboard.kpi(),
      redApi.dashboard.alertAttivi(30),
      redApi.dashboard.pagamentiScadenza(30),
      dashboardApi.agendaRed(30),
    ]).then(([k, a, pag, agenda]) => {
      setKpi(k.data)
      setAlerts(a.data)
      setPagamenti(pag.data)
      setAgendaNote(agenda.data?.note ?? [])
    }).catch((err) => {
      setError(err?.response?.data?.detail || err?.message || 'Errore nel caricamento dashboard')
    })
  }, [])

  if (error) return (
    <div className="p-8">
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700">
        <p className="font-semibold">Errore caricamento dashboard RED</p>
        <p className="text-sm mt-1 text-red-600">{error}</p>
      </div>
    </div>
  )
  if (!kpi) return <div className="p-8 text-gray-400">Caricamento dashboard...</div>

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard RED — Ricostruzione Marche</h1>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate('/red')}
            className="px-4 py-1.5 text-sm bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 flex items-center gap-1.5 shadow-sm"
          >
            🏘️ Portfolio Immobiliare
          </button>
          <ExportXlsxButton large />
          <button
            onClick={handleStampaPdf}
            disabled={generatingPdf}
            className="px-4 py-1.5 text-sm bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-800 flex items-center gap-1.5 shadow-sm disabled:opacity-50"
            title="Genera PDF A3 orizzontale con KPI e grafici"
          >
            <Printer size={15} /> {generatingPdf ? 'Generazione...' : 'Stampa PDF'}
          </button>
          <button
            onClick={() => apriModalInvio('whatsapp')}
            className="px-4 py-1.5 text-sm bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 flex items-center gap-1.5 shadow-sm"
            title="Invia il PDF via WhatsApp"
          >
            <MessageCircle size={15} /> Invia WhatsApp
          </button>
          <button
            onClick={() => apriModalInvio('email')}
            className="px-4 py-1.5 text-sm bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 shadow-sm"
            title="Invia il PDF via Email"
          >
            <Mail size={15} /> Invia Email
          </button>
        </div>
      </div>

      {/* KPI principali */}
      <div ref={kpiRef} className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPICard
          label="Totale investimento"
          value={EUR.format(kpi.totale_acquisizione)}
          sub={[
            { label: 'Prezzo acquisto', value: EUR.format(kpi.totale_prezzi_acquisto) },
            { label: 'Spese notarili', value: EUR.format(kpi.totale_spese_notarili) },
            { label: 'Imposte', value: EUR.format(kpi.totale_imposte) },
            { label: 'Costi immobili', value: EUR.format(kpi.totale_costi_extra) },
          ]}
        />
        <KPICard label="Totale immobili" value={String(kpi.n_immobili)} />
        <KPICard label="Acconti da pagare" value={EUR.format(kpi.totale_acconti_residui)} color="text-amber-700" />
        <KPICard
          label="Residuo da pagare"
          value={EUR.format(kpi.totale_saldi_residui)}
          color="text-orange-700"
          expandable={kpi.dettaglio_residuo.map(r => ({
            key: r.immobile_id,
            label: r.nome,
            value: EUR.format(r.importo),
            onClick: () => navigate(`/red/${r.immobile_id}`),
          }))}
        />
        <KPICard label="Spese notarili" value={EUR.format(kpi.totale_spese_notarili)} color="text-gray-800" />
        <KPICard label="Totale imposte" value={EUR.format(kpi.totale_imposte)} color="text-gray-800" />
        <KPICard label="Costi immobili" value={EUR.format(kpi.totale_costi_extra)} color="text-gray-800" />
        <KPICard label="Contributo USR" value={EUR.format(kpi.totale_usr_approvato)} color="text-blue-700" />
        <KPICard label="Erogato USR" value={EUR.format(kpi.totale_erogato_usr)} color="text-green-700" />
        <KPICard label="Residuo USR" value={EUR.format(kpi.residuo_usr)} color="text-orange-700" />
      </div>

      {agendaNote.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Agenda RED — Prossimi Promemoria</h2>
          <div className="space-y-2 max-h-96 overflow-auto">
            {agendaNote.map(n => (
              <Link
                key={n.id}
                to="/red/agenda"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 no-underline"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#1E40AF14', color: '#1E40AF' }}>
                  <BellRing size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 m-0">{n.titolo}</p>
                  <p className="text-xs text-gray-400 m-0">{n.immobile_nome}</p>
                </div>
                <div className="text-xs text-gray-500 font-mono whitespace-nowrap">
                  {new Date(n.data_ora).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Grafici dinamici */}
      <div ref={graficiRef}>
        {grafici && <GraficiRed data={grafici} />}
      </div>

      {/* Stati contrattuali */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Stato contrattuale immobili</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-700">{kpi.n_per_stato_contrattuale?.trattativa ?? 0}</div>
            <div className="text-xs text-gray-500 mt-1">In trattativa</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-700">{kpi.n_per_stato_contrattuale?.compromesso ?? 0}</div>
            <div className="text-xs text-gray-500 mt-1">Compromesso</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-700">{kpi.n_per_stato_contrattuale?.rogito ?? 0}</div>
            <div className="text-xs text-gray-500 mt-1">Rogito completato</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <h2 className="text-base font-semibold text-gray-800 mb-4">
            Alert attivi — prossimi 30 giorni
            {alerts.length > 0 && (
              <span className="ml-2 text-xs font-medium bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{alerts.length}</span>
            )}
          </h2>
          <div className="space-y-2 max-h-96 overflow-auto">
            {alerts.map(alert => {
              const cfg = URGENZA_CONFIG[alert.urgenza]
              return (
                <div key={alert.id}
                  onClick={() => navigate(`/red/${alert.immobile_id}`)}
                  className={`flex items-start justify-between p-3 rounded-lg border cursor-pointer hover:shadow-sm ${cfg.bg} ${cfg.border}`}>
                  <div className="flex-1 mr-3">
                    <p className={`text-sm font-medium ${cfg.text}`}>{alert.titolo}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{alert.immobile_nome} · {alert.immobile_comune}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-xs font-bold ${cfg.text}`}>
                      {alert.giorni_mancanti <= 0 ? 'SCADUTA' : `${alert.giorni_mancanti}gg`}
                    </div>
                    <div className="text-xs text-gray-400">{new Date(alert.data_scadenza).toLocaleDateString('it-IT')}</div>
                  </div>
                </div>
              )
            })}
            {alerts.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">Nessun alert nei prossimi 30 giorni</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Pagamenti in scadenza — 30 giorni</h2>
          <div className="space-y-2 max-h-96 overflow-auto">
            {pagamenti.map(p => (
              <div key={p.id} className={`flex items-center justify-between p-3 rounded-lg border ${p.in_ritardo ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <div>
                  <p className="text-sm font-medium text-gray-800">{p.immobile_nome}</p>
                  <p className="text-xs text-gray-500">{p.tipologia.replace(/_/g, ' ')}</p>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">{EUR.format(p.importo)}</div>
                  <div className={`text-xs ${p.in_ritardo ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                    {p.in_ritardo ? `SCADUTO da ${Math.abs(p.giorni_mancanti)}gg` : `tra ${p.giorni_mancanti}gg`}
                  </div>
                </div>
              </div>
            ))}
            {pagamenti.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">Nessun pagamento in scadenza</p>
            )}
          </div>
        </div>
      </div>

      {invioModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setInvioModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {canaleInvio === 'whatsapp' ? '💬 Invia PDF via WhatsApp' : '✉️ Invia PDF via Email'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">Seleziona i destinatari (lista Operatori Avviso — Impostazioni RED)</p>
            <div className="space-y-1.5 max-h-72 overflow-auto mb-4">
              {contattiInvio.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  {canaleInvio === 'whatsapp' ? 'Nessun operatore con numero WhatsApp configurato' : 'Nessun operatore con email configurata'}
                </p>
              ) : (
                contattiInvio.map(c => (
                  <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm ${!c.attivo ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={destinatariSelezionati.has(c.id)}
                      onChange={() => toggleContattoInvio(c.id)}
                    />
                    <span>{c.nome}</span>
                    {!c.attivo && <span className="text-xs text-gray-400">(disattivato)</span>}
                    <span className="text-xs text-gray-400 ml-auto">{canaleInvio === 'whatsapp' ? c.telefono_whatsapp : c.email}</span>
                  </label>
                ))
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setInvioModalOpen(false)} className="btn-secondary flex-1">Annulla</button>
              <button
                onClick={handleInvia}
                disabled={destinatariSelezionati.size === 0 || sendingInvio}
                className="btn-primary flex-1"
              >
                {sendingInvio ? 'Invio...' : `Invia (${destinatariSelezionati.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

const KPICard: React.FC<{
  label: string
  value: string
  color?: string
  bg?: string
  sub?: { label: string; value: string }[]
  expandable?: { key: string; label: string; value: string; onClick?: () => void }[]
}> = ({ label, value, color = 'text-gray-900', bg = 'bg-white', sub, expandable }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={`${bg} rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200`}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      {sub && sub.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
          {sub.map(s => (
            <div key={s.label} className="flex justify-between text-[11px] text-gray-500">
              <span>{s.label}</span>
              <span className="font-medium text-gray-600">{s.value}</span>
            </div>
          ))}
        </div>
      )}
      {expandable && expandable.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className="text-[11px] text-blue-600 hover:underline"
          >
            {open ? '▴ Nascondi dettaglio' : `▾ Da cosa nasce (${expandable.length})`}
          </button>
          {open && (
            <div className="mt-1.5 space-y-1 max-h-40 overflow-auto">
              {expandable.map(e => (
                <div
                  key={e.key}
                  onClick={e.onClick}
                  className={`flex justify-between text-[11px] ${e.onClick ? 'cursor-pointer hover:text-blue-700' : ''} text-gray-600`}
                >
                  <span className="truncate mr-2">{e.label}</span>
                  <span className="font-medium shrink-0">{e.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ChartCard: React.FC<{ titolo: string; children: React.ReactNode; full?: boolean }> = ({ titolo, children, full }) => (
  <div className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-lg transition-shadow duration-200 ${full ? 'lg:col-span-2' : ''}`}>
    <h2 className="text-base font-semibold text-gray-800 mb-4">{titolo}</h2>
    {children}
  </div>
)

const GraficiRed: React.FC<{ data: GraficiRedData }> = ({ data }) => {
  const nessunDato = (arr?: GraficoVoce[]) => !arr || arr.length === 0
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900">📊 Grafici Portfolio</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard titolo="Distribuzione livello operativo">
          {nessunDato(data.per_livello_operativo) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.per_livello_operativo} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3} strokeWidth={0}>
                  {data.per_livello_operativo.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard titolo="Immobili per comune">
          {nessunDato(data.per_comune) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.per_comune} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
                <Tooltip content={<ComuneTooltip />} />
                <Bar dataKey="value" name="Immobili" fill="#F97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard titolo="Stato contrattuale">
          {nessunDato(data.per_stato_contrattuale) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.per_stato_contrattuale} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={3} strokeWidth={0}>
                  {data.per_stato_contrattuale.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard titolo="Prezzo acquisto totale per comune (€)">
          {nessunDato(data.prezzo_per_comune) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.prezzo_per_comune} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => EUR.format(v)} />
                <Bar dataKey="value" name="Prezzo acquisto" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard titolo="Pagamenti: previsto vs effettivo per mese (€)" full>
          {nessunDato(data.pagamenti_per_mese as unknown as GraficoVoce[]) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.pagamenti_per_mese} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => EUR.format(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="previsto" name="Previsto" stroke="#1E40AF" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="effettivo" name="Effettivo" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard titolo="Immobili assegnati per professionista">
          {nessunDato(data.professionisti_top) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.professionisti_top} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" name="Immobili" fill="#ec4899" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  )
}

const EmptyChart: React.FC = () => (
  <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">Nessun dato disponibile</div>
)

const ComuneTooltip: React.FC<{ active?: boolean; payload?: { payload: ComuneVoce }[] }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const { label, value, immobili } = payload[0].payload
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 max-w-xs">
      <div className="text-sm font-semibold text-gray-800 mb-1.5">{label} · {value} immobil{value === 1 ? 'e' : 'i'}</div>
      <div className="space-y-0.5">
        {immobili.map(nome => <div key={nome} className="text-xs text-gray-600">{nome}</div>)}
      </div>
    </div>
  )
}

export default RedDashboard
